import { BaseTable, Button, Card, TableHeader } from "components"
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io"
import type {
  MovementControlRow,
  MovementSortKey,
  MovementSortState
} from "../types/inventoryMovement"
import { formatNumberId, smartNumber } from "../utils/inventoryMovementHelpers"

type MovementControlTableProps = {
  rows: MovementControlRow[]
  sort: MovementSortState
  onToggleSort: (key: MovementSortKey) => void
  onExportXls: () => void
  onExportPdf: () => void
  onPrint: () => void
  onOpenAudit: (row: MovementControlRow) => void
}

function SortTh({
  label,
  skey,
  sort,
  onToggle,
  align,
  className = ""
}: {
  label: string
  skey: MovementSortKey
  sort: MovementSortState
  onToggle: (k: MovementSortKey) => void
  align?: "left" | "right" | "center"
  className?: string
}) {
  const active = sort.key === skey
  const flex =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start"
  return (
    <th
      className={`cursor-pointer select-none ${className}`}
      onClick={() => onToggle(skey)}>
      <div className={`flex items-center gap-1.5 ${flex}`}>
        <span className="text-xs font-bold uppercase leading-tight">
          {label}
        </span>
        {active ? (
          sort.asc ? (
            <IoIosArrowUp className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <IoIosArrowDown className="h-4 w-4 shrink-0" aria-hidden />
          )
        ) : null}
      </div>
    </th>
  )
}

function StatusBadge({ status }: { status: MovementControlRow["status"] }) {
  if (status === "OK") {
    return (
      <span className="badge badge-success badge-sm font-bold whitespace-nowrap">
        OK
      </span>
    )
  }
  if (status === "WARNING") {
    return (
      <span className="badge badge-warning badge-sm font-bold whitespace-nowrap">
        WARNING
      </span>
    )
  }
  return (
    <span className="badge badge-error badge-sm font-bold whitespace-nowrap">
      ERROR
    </span>
  )
}

export function MovementControlTable({
  rows,
  sort,
  onToggleSort,
  onExportXls,
  onExportPdf,
  onPrint,
  onOpenAudit
}: MovementControlTableProps) {
  return (
    <Card className="shadow-sm">
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-[#dadada] px-4 py-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wide text-base-content sm:text-base">
            Inventory Movement Control Report
          </h3>
          <p className="mt-1 text-xs text-base-content/60">
            Formula: Previous Balance + IN - OUT = Closing Balance
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={onExportXls}>
            Export XLS
          </Button>
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={onExportPdf}>
            Export PDF
          </Button>
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={onPrint}>
            Print
          </Button>
        </div>
      </div>

      <Card.Body className="p-0">
        <div className="max-h-[min(660px,calc(100vh-320px))] min-h-[200px] overflow-auto">
          <BaseTable className="min-w-[1900px] border-collapse text-sm [&_td]:align-middle [&_th]:align-middle">
            <TableHeader className="sticky top-0 z-[5]">
              <tr>
                <th className="sticky left-0 z-[6] min-w-[56px] bg-black text-xs font-bold uppercase">
                  No
                </th>
                <SortTh
                  label="Date"
                  skey="date"
                  sort={sort}
                  onToggle={onToggleSort}
                  className="sticky left-14 z-[6] min-w-[132px] bg-black shadow-[3px_0_5px_rgba(0,0,0,0.15)]"
                />
                <SortTh
                  label="Transaction"
                  skey="transactionType"
                  sort={sort}
                  onToggle={onToggleSort}
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Reference"
                  skey="reference"
                  sort={sort}
                  onToggle={onToggleSort}
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Location"
                  skey="location"
                  sort={sort}
                  onToggle={onToggleSort}
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Opening"
                  skey="opening"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="IN"
                  skey="stockIn"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="OUT"
                  skey="stockOut"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Expected Closing"
                  skey="expectedClosing"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Actual Closing"
                  skey="actualClosing"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Variance"
                  skey="variance"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Cost"
                  skey="cost"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Value"
                  skey="value"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                  className="bg-black text-xs font-bold uppercase"
                />
                <SortTh
                  label="Status"
                  skey="status"
                  sort={sort}
                  onToggle={onToggleSort}
                  className="bg-black text-xs font-bold uppercase"
                />
                <th className="bg-black text-xs font-bold uppercase">
                  Control Note
                </th>
                <th className="bg-black text-xs font-bold uppercase">
                  Created By
                </th>
                <th className="bg-black text-xs font-bold uppercase">
                  Action
                </th>
              </tr>
            </TableHeader>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={`${row.reference}-${row.date}-${idx}`}>
                  <td className="sticky left-0 z-[1] min-w-[56px] bg-base-100 font-semibold">
                    {idx + 1}
                  </td>
                  <td className="sticky left-14 z-[1] min-w-[132px] bg-base-100 font-medium shadow-[3px_0_5px_rgba(0,0,0,0.06)]">
                    {row.date}
                  </td>
                  <td>{row.transactionType}</td>
                  <td className="font-medium">{row.reference}</td>
                  <td>{row.location}</td>
                  <td className="text-right tabular-nums">
                    {formatNumberId(row.opening)}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatNumberId(row.stockIn)}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatNumberId(row.stockOut)}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatNumberId(row.expectedClosing)}
                  </td>
                  <td className="text-right tabular-nums font-semibold">
                    {formatNumberId(row.actualClosing)}
                  </td>
                  <td className="text-right tabular-nums">
                    {formatNumberId(row.variance)}
                  </td>
                  <td
                    className="text-right font-bold tabular-nums"
                    title={formatNumberId(row.cost)}>
                    {smartNumber(row.cost)}
                  </td>
                  <td
                    className="text-right font-bold tabular-nums"
                    title={formatNumberId(row.value)}>
                    {smartNumber(row.value)}
                  </td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="max-w-[200px] text-xs text-base-content/80">
                    {row.controlNote}
                  </td>
                  <td className="text-sm">{row.createdBy}</td>
                  <td>
                    <Button
                      type="button"
                      color="outline-secondary"
                      size="sm"
                      className="btn-xs"
                      onClick={() => onOpenAudit(row)}>
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </BaseTable>
        </div>
      </Card.Body>
    </Card>
  )
}
